import Draggable from 'gsap/Draggable';
import {TweenMax, Circ, Power2} from 'gsap';

const EventEmitter = require('eventemitter3');

const win = window;

const __getRatio = orientation => {
    return orientation === 'x' ? 0.55 : 0.35;
};

const __getSize = (orientation, rect) => {
    return orientation === 'x' ? rect.width : rect.height;
};

const __getSign = orientation => {
    return orientation === 'x' ? -1 : 1;
};

const __getDelta = (orientation, rect) => {
    return orientation === 'x' ? rect.dw : rect.dh;
};

const __getInitialPosition = (orientation, ui) => {
    return __getSign(orientation) * (ui.size + __getDelta(ui.orientation, ui) * 1.2);
};

const __getGoPosition = orientation => {
    return orientation === 'x' ? 1 : 0;
};

const _init = function(ui) {
    _setFunctionsByOrientation(ui.orientation);

    ui.btn = ui.el.querySelector('.js-button');
    ui.btnVisible = ui.el.querySelector('.js-button span');
    ui.line = ui.el.querySelector('.js-line');
    ui.bound = ui.btn.parentNode;
    ui.ratio = __getRatio(ui.orientation);
    ui.x = 0;
    ui.y = 0;

    ui.el.dataset.active = true;

    TweenMax.set(ui.line, {
        transformOrigin: '100% 100%',
        scaleX: 0
    });

    if (ui.orientation === 'x') {
        ui.el.classList.add('dragable-point--horisontal');
    } else {
        ui.el.classList.add('dragable-point--vertical');
    }

    ui._onResize = () => {
        const prevSize = ui.size;
        ui.size = __getSize(ui.orientation, ui.bound.getBoundingClientRect());
        ui.dw = ui.btn.getBoundingClientRect().width;
        ui.dh = ui.btn.getBoundingClientRect().height;
        if (!ui.draggable) return;
        __updatePosition(ui, prevSize);
    };

    ui._onResize();

    ui.draggable = Draggable.create(ui.btn, {
        type: 'x,y',
        bounds: ui.btn.parentNode,
        edgeResistance: 0.65,
        dragResistance: 0.1,
        throwProps: false,
        dragClickables: true,
        onDrag: function() {
            ui.el.dataset.active = true;
            TweenMax.killTweensOf(ui.btn);

            __setSyncPositionBack(ui, this);
        },
        onDragEnd: function() {
            const v = __liveSnap(ui, ui.orientation === 'x' ? this.x : this.y);
            TweenMax.to(ui, 0.35, {
                p: v === 0 ? -ui.dw : v,
                onUpdate: () => {
                    __syncPosition(ui);
                    this.update();
                },
                onComplete: () => {
                    ui.el.dataset.active = false;
                },
                ease: Power2.easeInOut
            });

            if (v === __getGoPosition(ui.orientation)) {
                ui._done = true;
                ui.emit('go');
                let newEvent;
                if (typeof Event === 'function') {
                    newEvent = new Event('go');
                } else {
                    newEvent = document.createEvent('Event');
                    newEvent.initEvent('go', true, true);
                }
                ui.el.dispatchEvent(newEvent);
            }
        }
    })[0];

    TweenMax.set(ui, {
        p: __getInitialPosition(ui.orientation, ui),
        onUpdate: () => {
            __syncPosition(ui);
        }
    });

    if (!ui._enabled) ui.draggable.enabled(false, ui.orientation);
};

let __liveSnap;
let __getClientPosition;
let __updatePosition;
let __resetPosition;
let __setSyncPositionBack;
let __syncPosition;
let __syncLine;
let __getBoundOffset;
let __getBoundMinOffset;

const _show = function(delay = 0) {
    console.log('show');
    const ui = this;
    if (ui._shown) return this;

    ui.el.dataset.active = true;
    TweenMax.to(ui, 1.4, {
        delay: delay,
        p: ui.size * ui.ratio,
        ease: Circ.easeOut,
        onUpdate: () => {
            __syncPosition(ui);
        },
        onComplete: () => {
            ui.el.dataset.active = false;
        }
    });

    TweenMax.to(ui.el, 0.15, {
        opacity: 1
    });

    ui._shown = true;
    return this;
};

const _hide = function(delay = 0) {
    const ui = this;
    if (!ui._shown) return this;

    ui.el.dataset.active = true;
    TweenMax.to(ui, 0.3, {
        delay: delay,
        p: -__getDelta(ui.orientation, ui) * 2,
        onUpdate: () => {
            __syncPosition(ui);
        }
    });

    TweenMax.to(ui.el, 0.3, {
        delay: 0.2,
        opacity: 0
    });
    ui._shown = false;
    return this;
};

const _enable = function() {
    const ui = this;
    if (ui._enabled) return this;

    _addEventListeners(ui);
    ui.draggable.enabled(true, ui.orientation);
    ui._enabled = true;

    if (!ui._shown) __resetPosition(ui);

    return this;
};

const _disable = function() {
    const ui = this;
    if (!ui._enabled) return this;

    _addEventListeners(ui, false);
    ui.draggable.enabled(false, ui.orientation);
    ui._enabled = false;

    if (!ui._shown) __resetPosition(ui);

    return this;
};

const _destroy = function() {
    const ui = this;
    ui.disable();
    ui.removeAllListeners();

    delete ui.el.data.uiController;
    return this;
};

const _setOrientation = function(orientation) {
    const ui = this;
    if (ui.orientation === orientation) return;

    TweenMax.killTweensOf(ui);
    ui.el.dataset.active = false;

    const prevSize = ui.size;
    ui.ratio = __getRatio(orientation);
    ui.size = __getSize(orientation, ui.bound.getBoundingClientRect());

    __resetPosition(ui);
    _setFunctionsByOrientation(orientation);

    if (ui.draggable) {
        __updatePosition(ui, prevSize);
        if (ui._enabled) {
            ui.draggable.enabled(false, ui.orientation);
            ui.draggable.enabled(true, orientation);
            ui.draggable.type = orientation;
        }
    }

    ui.orientation = orientation;
};

const _setFunctionsByOrientation = function(orientation) {
    switch (orientation) {
        case 'x':
            __liveSnap = function(ui, value) {
                if (value < ui.size * ui.ratio * 0.95) return ui.size * ui.ratio;
                return 1;
            };

            __getClientPosition = e => {
                return e.clientX;
            };

            __getBoundOffset = function(ui) {
                return ui.bound.getBoundingClientRect().right;
            };

            __getBoundMinOffset = function(ui) {
                return ui.bound.getBoundingClientRect().left;
            };

            __syncPosition = function(ui) {
                TweenMax.set(ui.btn, {
                    x: ui.p,
                    y: 0
                });
                __syncLine(ui);
            };

            __updatePosition = function(ui, prevSize) {
                TweenMax.to(ui, 0.1, {
                    p: __liveSnap(ui, (ui.p * ui.size) / prevSize),
                    onUpdate: () => {
                        __syncPosition(ui);
                        ui.draggable.update();
                    }
                });
            };

            __resetPosition = function(ui) {
                TweenMax.set(ui, {
                    p: __getInitialPosition(orientation, ui),
                    onUpdate: () => {
                        __syncPosition(ui);
                    }
                });
            };

            __setSyncPositionBack = function(ui, draggable) {
                TweenMax.set(ui, {
                    p: draggable.x
                });
                TweenMax.set(ui.btn, {
                    y: 0
                });
                __syncLine(ui);
            };

            __syncLine = function(ui) {
                TweenMax.set(ui.line, {
                    scaleX: (ui.p + __getDelta(ui.orientation, ui) / 2 - 6) / ui.size,
                    scaleY: 1,
                    transformOrigin: '0 50%'
                });
            };
            break;

        case 'y':
            __liveSnap = function(ui, value) {
                if (value > ui.size * ui.ratio * 0.95) return ui.size * ui.ratio;
                return 0;
            };

            __getClientPosition = e => {
                return e.clientY;
            };

            __getBoundOffset = function(ui) {
                return ui.bound.getBoundingClientRect().top;
            };

            __getBoundMinOffset = function(ui) {
                return ui.bound.getBoundingClientRect().top;
            };

            __syncPosition = function(ui) {
                TweenMax.set(ui.btn, {
                    y: ui.p,
                    x: 0
                });
                __syncLine(ui);
            };

            __updatePosition = function(ui, prevSize) {
                TweenMax.to(ui, 0.1, {
                    p: __liveSnap(ui, (ui.p * ui.size) / prevSize),
                    onUpdate: () => {
                        __syncPosition(ui);
                        ui.draggable.update();
                    }
                });
            };

            __resetPosition = function(ui) {
                TweenMax.set(ui, {
                    p: __getInitialPosition(orientation, ui),
                    onUpdate: () => {
                        __syncPosition(ui);
                    }
                });
            };

            __setSyncPositionBack = function(ui, draggable) {
                TweenMax.set(ui, {
                    p: draggable.y
                });
                TweenMax.set(ui.btn, {
                    x: 0
                });
                __syncLine(ui);
            };

            __syncLine = function(ui) {
                TweenMax.set(ui.line, {
                    scaleY: 1 - (ui.p + __getDelta(ui.orientation, ui) - 1) / ui.size,
                    scaleX: 1
                });
            };
            break;
    }
};

const _addEventListeners = function(ui, b) {
    if (b === false) {
        win.removeEventListener('resize', ui._onResize);
        ui.btn.removeEventListener('click', ui._onClick);
        if (ui.handleOver) {
            ui.el.removeEventListener('mouseover', ui._onHover);
            ui.el.removeEventListener('mouseout', ui._onOut);
            ui.el.removeEventListener('mouseleave', ui._onOut);
        }
        return;
    }

    ui._onClick = () => {
        if (!ui.handleClick) return;
        ui.emit('go');
        let newEvent;
        if (typeof Event === 'function') {
            newEvent = new Event('go');
        } else {
            newEvent = document.createEvent('Event');
            newEvent.initEvent('go', true, true);
        }
        ui.el.dispatchEvent(newEvent);
    };

    ui._onHover = evt => {
        let e = evt || win.event;

        if (!e) return;
        if (e.originalEvent) {
            e = e.originalEvent;
        }
        let p = __getClientPosition(e) /* - ui.dw / 2 */ - __getBoundOffset(ui);

        p = __getClientPosition(e) < __getBoundMinOffset(ui) + ui.dw ? 0 : p;

        ui.el.dataset.active = true;

        TweenMax.to(ui, 0.35, {
            p: p,
            onUpdate: () => {
                __syncPosition(ui);
            },
            ease: Power2.easeInOut
        });
    };

    ui._onTouch = evt => {
        let e = evt || win.event;

        if (!e) return;
        if (e.originalEvent) {
            e = e.originalEvent;
        }
        if (!e.touches || e.touches.length < 1) return;
        const p = __getClientPosition(e.touches[e.touches.length - 1]) - ui.dw / 2;

        ui.el.dataset.active = true;
        TweenMax.to(ui, 0, {
            p: p,
            onUpdate: () => {
                __syncPosition(ui);
            },
            ease: Power2.easeInOut
        });
    };

    ui._onOut = () => {
        if (ui._done) return;

        TweenMax.to(ui, 0.35, {
            p: ui.size * ui.ratio,
            onUpdate: () => {
                __syncPosition(ui);
            },
            onComplete: () => {
                ui.el.dataset.active = false;
            },
            ease: Power2.easeInOut
        });
    };

    win.addEventListener('resize', ui._onResize);
    ui.btn.addEventListener('click', ui._onClick);
    if (ui.handleOver) {
        ui.el.addEventListener('mouseover', ui._onHover);
        ui.el.addEventListener('mouseout', ui._onOut);
        ui.el.addEventListener('mouseleave', ui._onOut);
    }

    ui.el.addEventListener('touchstart', ui._onTouch);
    ui.el.addEventListener('touchend', ui._onOut);
};

module.exports = (el, setOrientation = 'y') => {
    if (el.data && el.data.uiController) return el.data.uiController;

    const ui = new EventEmitter();
    ui.el = el;
    ui.orientation = setOrientation;
    ui.handleClick = true;
    ui.handleOver = false;

    ui.el.data = ui.el.data || {};
    ui.el.data.uiController = ui;

    ui.show = _show.bind(ui);
    ui.hide = _hide.bind(ui);
    ui.enable = _enable.bind(ui);
    ui.disable = _disable.bind(ui);
    ui.destroy = _destroy.bind(ui);
    ui.setOrientation = _setOrientation.bind(ui);
    ui.progress = 0;

    _init(ui);

    return ui;
};
